import { MoreOutlined } from '@ant-design/icons';
import {
    Avatar,
    Box,
    ButtonBase,
    CardContent,
    ClickAwayListener,
    Grid,
    IconButton,
    Paper,
    Popper,
    Stack,
    Typography,
    useMediaQuery
} from '@mui/material';
import { Theme } from '@mui/material/styles';
// material-ui
import { useTheme } from '@mui/material/styles';
// assets
import Transitions, { TransitionsProps } from 'components/@extended/Transitions';
// project import
import MainCard from 'components/MainCard';
import InviteButton from 'components/invitation/InviteButton';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StateType } from 'store/reducers';
import { logout } from 'store/reducers/auth';
import { CustomTheme } from 'themes';
import { displayName } from 'utils/user';
import ProfileTab from './ProfileTab';

// tab panel wrapper

export interface TabPanelProps extends PropsWithChildren {
    index: number;
    value: number;
    [other: string]: any;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }): JSX.Element => {
    return (
        <div role="tabpanel" hidden={value !== index} id={`profile-tabpanel-${index}`} aria-labelledby={`profile-tab-${index}`} {...other}>
            {value === index && children}
        </div>
    );
};

function a11yProps(index: number) {
    return {
        id: `profile-tab-${index}`,
        'aria-controls': `profile-tabpanel-${index}`
    };
}

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

const Profile: React.FC = (): JSX.Element => {
    const theme: CustomTheme = useTheme();
    const anchorRef: React.MutableRefObject<HTMLButtonElement | null> = useRef(null);
    const dispatch = useDispatch();
    const loggedUser = useSelector((state: StateType) => state.auth);

    const matchesXs = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const matchesXl = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));

    const [open, setOpen] = useState(false);
    const [userDisplayName, setUserDisplayName] = useState<string>(' ');
    const [userPhone, setUserPhone] = useState<string>('');

    useEffect(() => {
        if (loggedUser) {
            setUserDisplayName(displayName(loggedUser));
            setUserPhone(loggedUser.phone ? (loggedUser.phone.startsWith('+') ? loggedUser.phone : `+${loggedUser.phone}`) : '');
        }
    }, [loggedUser]);

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: MouseEvent | TouchEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
            return;
        }
        setOpen(false);
    };

    const handleLogout = () => {
        dispatch(logout());
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 0.75 }}>
            {!matchesXs ? (
                <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 0.5 }}>
                    {!matchesXl && <InviteButton />}
                    <ButtonBase
                        sx={{
                            p: 0.25,
                            borderRadius: 1
                        }}
                        aria-label="open profile"
                        ref={anchorRef}
                        aria-controls={open ? 'profile-grow' : undefined}
                        aria-haspopup="true"
                        onClick={handleToggle}
                    >
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 0.5 }}>
                            <Avatar alt={userDisplayName} sx={{ width: 32, height: 32, bgcolor: theme.palette.success[500] }}>
                                {userDisplayName?.substring(0, 1)?.toUpperCase()}
                            </Avatar>
                            <Stack direction="column">
                                <Typography variant="subtitle1">{userDisplayName}</Typography>
                                {userPhone && <Typography variant="subtitle2">{userPhone}</Typography>}
                            </Stack>
                        </Stack>
                    </ButtonBase>
                </Stack>
            ) : (
                <Box sx={{ flexShrink: 0, ml: 0.75 }}>
                    <IconButton
                        component="span"
                        disableRipple
                        sx={{
                            bgcolor: open ? 'grey.300' : 'grey.100'
                        }}
                        ref={anchorRef}
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        onClick={handleToggle}
                        color="inherit"
                    >
                        <MoreOutlined />
                    </IconButton>
                </Box>
            )}
            <Popper
                placement="bottom-end"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 9]
                            }
                        }
                    ]
                }}
            >
                {({ TransitionProps }: Omit<Omit<TransitionsProps, 'type'>, 'in'>) => (
                    <Transitions type="fade" in={open} {...TransitionProps}>
                        {open && (
                            <Paper
                                sx={{
                                    boxShadow: theme.customShadows.z1,
                                    width: 290,
                                    minWidth: 240,
                                    maxWidth: 290,
                                    [theme.breakpoints.down('md')]: {
                                        maxWidth: 250
                                    }
                                }}
                            >
                                <ClickAwayListener onClickAway={handleClose}>
                                    <MainCard elevation={0} border={false} content={false}>
                                        <CardContent sx={{ px: 2.5, pt: 3 }}>
                                            <Grid container justifyContent="space-between" alignItems="center">
                                                <Grid item>
                                                    <Stack direction="row" spacing={1.25} alignItems="center">
                                                        <Avatar
                                                            alt={userDisplayName}
                                                            sx={{ width: 32, height: 32, bgcolor: theme.palette.success[500] }}
                                                        >
                                                            {userDisplayName?.substring(0, 1)}
                                                        </Avatar>
                                                        <Stack>
                                                            <Typography variant="h6">{userDisplayName}</Typography>
                                                        </Stack>
                                                    </Stack>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                        <ProfileTab handleLogout={handleLogout} />
                                    </MainCard>
                                </ClickAwayListener>
                            </Paper>
                        )}
                    </Transitions>
                )}
            </Popper>
        </Box>
    );
};

export default Profile;
